import React from 'react';
import { motion } from 'framer-motion';
import ContentPage from '../ContentPage';

const Retainership: React.FC = () => {
  // Features for each retainer level
  const standardFeatures = [
    { text: "Priority listing placement", icon: "📈" },
    { text: "Monthly performance reports", icon: "📊" },
    { text: "Dedicated support channel", icon: "💬" },
    { text: "Featured in monthly newsletter", icon: "📣" }
  ];
  
  const premiumFeatures = [
    { text: "All Standard features", icon: "✅" },
    { text: "Professional product photography", icon: "📸" },
    { text: "SEO optimization for listings", icon: "🔍" },
    { text: "Social media promotion", icon: "📱" },
    { text: "Quarterly strategy consultation", icon: "📋" }
  ];

  return (
    <ContentPage title="Retainership Programs" accent="yellow">
      {/* Introduction with decorative elements */}
      <div className="relative mb-10">
        <div className="absolute -top-8 -right-8 w-32 h-32 bg-gradient-to-bl from-yellow-500/20 to-purple-500/20 rounded-full blur-xl opacity-70"></div>
        <p className="mb-4 text-lg relative z-10">
          SokoVibe offers exclusive retainership programs designed to provide ongoing support and premium services to our valued sellers. These programs are tailored to help you maximize your presence and sales on our platform.
        </p>
      </div>
      
      {/* Pricing cards with animation */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mt-8">
        {/* Standard Retainer Card */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="relative overflow-hidden rounded-2xl shadow-lg"
        >
          {/* Card header */}
          <div className="bg-gradient-to-r from-purple-600 to-purple-800 px-6 py-8 text-white">
            <div className="absolute top-0 right-0 w-20 h-20 bg-white opacity-10 rounded-bl-full"></div>
            <div className="flex items-center mb-4">
              <span className="text-3xl mr-3">👥</span>
              <h3 className="text-2xl font-bold">Standard Retainer</h3>
            </div>
            <div className="flex items-baseline">
              <span className="text-3xl font-bold">$199</span>
              <span className="text-sm ml-2 opacity-80">/month</span>
            </div>
            <p className="mt-2 text-sm opacity-80">Perfect for new and growing sellers</p>
          </div>
          
          {/* Card content */}
          <div className="p-6 bg-white">
            <ul className="space-y-4 mb-6">
              {standardFeatures.map((feature, index) => (
                <motion.li 
                  key={index}
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.1 * index }}
                  className="flex items-start"
                >
                  <span className="text-xl mr-3 text-purple-600">{feature.icon}</span>
                  <span className="text-gray-700">{feature.text}</span>
                </motion.li>
              ))}
            </ul>
            <button className="w-full py-3 bg-gradient-to-r from-purple-700 to-purple-500 text-white rounded-lg hover:shadow-lg transition-all transform hover:scale-105 duration-200 font-medium">
              Learn More
            </button>
          </div>
          
          {/* Decorative corner shape */}
          <div className="absolute bottom-0 left-0 w-16 h-16 bg-purple-100 opacity-50 rounded-tr-full"></div>
        </motion.div>
        
        {/* Premium Retainer Card */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
          className="relative overflow-hidden rounded-2xl shadow-lg border-2 border-yellow-300"
        >
          {/* Popular badge */}
          <div className="absolute top-0 right-0 bg-yellow-500 text-white px-4 py-1 rounded-bl-lg z-10 font-medium shadow-sm">
            Most Popular
          </div>
          
          {/* Card header */}
          <div className="bg-gradient-to-r from-yellow-500 to-orange-500 px-6 py-8 text-white">
            <div className="absolute top-0 left-0 w-20 h-20 bg-white opacity-10 rounded-br-full"></div>
            <div className="flex items-center mb-4">
              <span className="text-3xl mr-3">⭐</span>
              <h3 className="text-2xl font-bold">Premium Retainer</h3>
            </div>
            <div className="flex items-baseline">
              <span className="text-3xl font-bold">$399</span>
              <span className="text-sm ml-2 opacity-80">/month</span>
            </div>
            <p className="mt-2 text-sm opacity-80">For established businesses seeking growth</p>
          </div>
          
          {/* Card content */}
          <div className="p-6 bg-white">
            <ul className="space-y-4 mb-6">
              {premiumFeatures.map((feature, index) => (
                <motion.li 
                  key={index}
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.1 * index }}
                  className="flex items-start"
                >
                  <span className="text-xl mr-3 text-yellow-600">{feature.icon}</span>
                  <span className="text-gray-700">{feature.text}</span>
                </motion.li>
              ))}
            </ul>
            <button className="w-full py-3 bg-gradient-to-r from-yellow-500 to-orange-500 text-white rounded-lg hover:shadow-lg transition-all transform hover:scale-105 duration-200 font-medium">
              Learn More
            </button>
          </div>
          
          {/* Decorative corner shape */}
          <div className="absolute bottom-0 right-0 w-16 h-16 bg-yellow-100 opacity-50 rounded-tl-full"></div>
        </motion.div>
      </div>
      
      {/* Enterprise section with unique shape */}
      <div className="mt-12 relative">
        <div className="absolute -top-10 -left-10 w-32 h-32 bg-gradient-to-tr from-purple-500/20 via-orange-500/10 to-yellow-500/20 rounded-full blur-xl opacity-70"></div>
        
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.4 }}
          className="relative z-10 p-8 bg-white rounded-xl shadow-md border border-gray-100 overflow-hidden"
        >
          {/* Decorative diagonal stripe */}
          <div className="absolute -right-20 -top-20 w-40 h-40 bg-gradient-to-br from-yellow-500/10 via-orange-500/10 to-purple-500/10 transform rotate-45"></div>
          
          <div className="flex flex-col md:flex-row md:items-center md:justify-between relative z-10">
            <div className="mb-6 md:mb-0 md:pr-6">
              <div className="flex items-center mb-3">
                <div className="w-1.5 h-6 bg-gradient-to-b from-purple-600 to-yellow-500 mr-3 rounded-full"></div>
                <h3 className="text-2xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-purple-800 via-orange-600 to-yellow-600">
                  Custom Enterprise Solutions
                </h3>
              </div>
              <p className="text-gray-700 md:max-w-xl">
                For established businesses with specific needs, we offer tailored enterprise solutions that can include custom integration, dedicated account management, and bespoke marketing campaigns.
              </p>
            </div>
            
            <button className="px-8 py-3 bg-gradient-to-r from-purple-700 via-orange-600 to-yellow-600 text-white rounded-lg hover:shadow-lg transition-all transform hover:scale-105 duration-200 font-medium whitespace-nowrap flex-shrink-0">
              Contact Us for Details
            </button>
          </div>
        </motion.div>
      </div>
      
      {/* Testimonial section */}
      <motion.div 
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.5, delay: 0.6 }}
        className="mt-12 p-6 bg-gradient-to-r from-purple-50 via-orange-50 to-yellow-50 rounded-xl border border-yellow-100 relative overflow-hidden"
      >
        <div className="absolute top-0 right-0 w-20 h-20 bg-yellow-300 opacity-10 rounded-bl-full"></div>
        <div className="absolute bottom-0 left-0 w-16 h-16 bg-purple-300 opacity-10 rounded-tr-full"></div>
        
        <blockquote className="relative z-10">
          <p className="text-lg italic text-gray-700 mb-4">
            "The Premium Retainer program has been a game-changer for our business. The professional photography and SEO optimization have significantly increased our visibility and sales on the platform."
          </p>
          <footer className="text-right">
            <p className="font-medium text-orange-700">— Aisha M., Premium Seller</p>
          </footer>
        </blockquote>
      </motion.div>
    </ContentPage>
  );
};

export default Retainership;
